import * as apiURL from "./apiURL";
import { savePostLoginRedirect } from "../postLoginRedirect";
// MEASUREMENT ONLY - Task 24731. Silent unless localStorage.apiTiming === "1".
import { isTimingEnabled, parseServerTiming, recordTiming } from "./apiTiming";

// Task 24900 - backup capture for server errors.
//
// The backend records every 5xx it produces into ZCRM_Master.ServerErrorLog and
// marks the response with X-CRMit-Error-Id. A 5xx WITHOUT that header never
// reached (or never finished in) PHP - a gateway error, a timeout, a proxy
// fault - so the server has no record of it. Those are posted to
// /servererrorlog/clientreport so they are not lost.
//
// Must never affect the caller: no await, every step guarded, and the error
// endpoint itself is never reported (a broken logger cannot loop).
const SERVER_ERROR_REPORT_PATH = "/servererrorlog/clientreport";
const SERVER_ERROR_MAX_TEXT = 20000;

const reportServerError = ({ response, url, method, requestBody, token, authUser }) => {
  try {
    if (String(url || "").startsWith(SERVER_ERROR_REPORT_PATH)) return;
    if (response?.headers?.get?.("X-CRMit-Error-Id")) return;

    const clone = response.clone();
    clone
      .text()
      .catch(() => "")
      .then((responseText) => {
        const truncate = (value) =>
          typeof value === "string" ? value.slice(0, SERVER_ERROR_MAX_TEXT) : null;

        return fetch(apiURL?.ApiURL()?.API_URL + SERVER_ERROR_REPORT_PATH, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            ...(token && { "x-token-key": token }),
            ...(!token &&
              authUser?.token && { Authorization: `Bearer ${authUser.token}` }),
          },
          body: JSON.stringify([
            {
              HttpStatus: response.status,
              HttpMethod: method,
              ApiPath: url,
              RequestBody: truncate(requestBody),
              ResponseBody: truncate(responseText),
              Message: response.statusText || null,
              PageUrl: window?.location?.pathname || null,
            },
          ]),
        });
      })
      .catch(() => {});
  } catch (_) {
    // never let error reporting break a request
  }
};

export const FetchAPI = async (
  url,
  method,
  userData,
  token,
  option,
  urlExtension,
  // Task 24695 - optional AbortSignal, so a long-running call can actually be
  // stopped rather than having its result quietly discarded. Additive and
  // last in the list, so no existing caller is affected; fetch ignores an
  // undefined signal.
  signal
) => {
  // If user data array, reduce to flat structure
  if (isTimingEnabled()) console.log("userData", userData);

  function flatten(a) {
    return Array.isArray(a) ? [].concat(...a.map(flatten)) : a;
  }

  const userObj =
    userData !== undefined && userData !== null ? flatten(userData) : undefined;

  // Build request body (no body for GET or when there's no data)
  const requestBody =
    method === "GET" || userObj === undefined ? null : JSON.stringify(userObj);

  // ----- GET URL handling -----
  if (method === "GET") {
    // check if userData contains a valid userData.Id
    if (option === "companiesHouse") {
      if (userData?.search) {
        url = url + `?q=${userData?.search}&items_per_page=3&start_index=1`;
      }
    } else if (userData?.id) {
      url = url + "/" + userData?.id;
    }
  }

  // ----- POST URL handling -----
  if (isTimingEnabled()) console.log("method", method);
  if (method === "POST") {
    // check if userData contains a valid userData.Id
    const firstId =
      Array.isArray(userData) && userData?.length ? userData[0]?.id : undefined;

    if (isTimingEnabled()) console.log("userData?.[0]?.id", firstId);

    // single object payload with an id
    if (userData && !Array?.isArray(userData) && userData?.id) {
      url = url + "/" + userData?.id;
    }
    // array payload – only if we actually HAVE a first id
    else if (firstId) {
      url = url + "/" + firstId;
    }

    // optional extra path segment
    if (urlExtension) {
      url = url + "/" + urlExtension;
    }
  }

  // Build final URL *after* all mutations
  const combinedURL = apiURL?.ApiURL()?.API_URL + url;

  // Debug logging, opt-in. The old localhost guard here was commented out, so these
  // ran on every request in production with full payload objects attached.
  if (isTimingEnabled()) {
    console.log("🔍 Full URL:", combinedURL);
    console.log("🔍 After flatten - userObj:", userObj);
    console.log("🔍 Request body:", requestBody, "Type:", typeof requestBody);
  }

  const authUser = JSON?.parse(localStorage?.getItem("authUser"));

  // MEASUREMENT ONLY - Task 24731
  const timingStart =
    typeof performance !== "undefined" ? performance.now() : Date.now();

  try {
    const response = await fetch(combinedURL, {
      method,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        // if token is passed explicitly add x-token-key header with token
        ...(token && { "x-token-key": token }),
        // otherwise fall back to bearer from authUser if present
        ...(!token &&
          authUser?.token && {
            Authorization: `Bearer ${authUser.token}`,
          }),
      },
      // if method is GET don't send body
      body: requestBody,
      signal,
    });

    // MEASUREMENT ONLY - Task 24731. Recorded here, before the 401 / 493-499
    // branches below, so early-return requests still produce a sample.
    try {
      const timingEnd =
        typeof performance !== "undefined" ? performance.now() : Date.now();
      recordTiming({
        url,
        method,
        status: response?.status,
        perceivedMs: timingEnd - timingStart,
        server: parseServerTiming(response?.headers?.get?.("Server-Timing")),
      });
    } catch (_) {
      // never let instrumentation break a request
    }

    // Task 24900 - report a 5xx the server did not log itself. Fire-and-forget,
    // reads a clone so the parse below is untouched.
    if (response?.status >= 500) {
      reportServerError({ response, url, method, requestBody, token, authUser });
    }

    const receivedData = await response.json();


    // Central 401 handling
    //
    // Task 24831 - A TOKEN-AUTHENTICATED CALL MUST NEVER TRIGGER THIS.
    //
    // A guest following an event link has no CRM session: they are
    // authenticated by an x-token-key passed per request, which is what the
    // `token` argument above is. When one of those calls came back 401 this
    // block cleared localStorage and hard-redirected to /login - throwing a
    // restaurant guest, mid pre-order, onto a login page for an account they
    // do not have and cannot create.
    //
    // That is what made Scoozi's party unable to order: clicking the Food
    // Menu tab fires token-authenticated reads, and one 401 among them ended
    // the session. It also explains the post_login_redirect left behind on
    // /manage-event/<token> - this block writes that key too, not just
    // AuthProtected.
    //
    // For a token call a 401 means "this token may not do that", which is
    // information for the caller, not a reason to destroy a session that was
    // never there. The response is returned and the caller decides.
    const var___is_token_authenticated_call = !!token;

    if (receivedData?.status === 401 && var___is_token_authenticated_call) {
      return receivedData;
    }

    // Change #100251 - the same for the public loyalty spinner page. Its API
    // answers 401 for "wrong staff PIN" and for "company cannot be found from
    // this address"; neither is a session expiring. Treated as one, a visitor
    // was logged out and thrown onto /login - including a customer who typed
    // a wrong PIN. The page shows the message itself.
    if (
      receivedData?.status === 401 &&
      window.location.pathname.startsWith("/loyalty-reward-spinner/")
    ) {
      return receivedData;
    }

    if (receivedData?.status === 401) {
      // 1) Remember where the user was trying to go
      try {
        const { pathname, search, hash } = window.location;
        const attempted = `${pathname}${search}${hash}`;
        console.log("attempted", attempted);
        // Change #100336 - saved with whose session it was (authUser is
        // still present here, it is burned in step 2).
        savePostLoginRedirect(attempted);
      } catch (_) {
        // ignore storage errors
      }

      // 2) Burn auth locally
      try {
        localStorage.removeItem("authUser");
        localStorage.removeItem("firebase_fcm_token");
      } catch (_) {
        // ignore storage errors
      }

      // 3) Converge on a clean logout. Auth is already cleared above, so a full
      //    navigation to /login lands cleanly (App boot won't restore the dead
      //    session) and cancels in-flight requests. Guard on pathname so a 401
      //    raised on the login page itself can't cause a redirect loop - the
      //    concern that previously left this disabled.
      if (
        window.location.pathname !== "/login" &&
        !window.location.pathname.startsWith("/logout")
      ) {
        window.location.href = "/login";
      }

      // Stop further processing – callers may get undefined
      return;
    }

    // Handle no-access codes (keep existing behaviour)
    // Change #100463 - not when already on a no-access page: background calls
    // made by that page would otherwise reload it over and over.
    if (
      receivedData?.status >= 493 &&
      receivedData?.status <= 499 &&
      !window.location.pathname.startsWith("/no-access/")
    ) {
      window.location.href = `/no-access/${receivedData.status}`;
    }

    return receivedData;
  } catch (error) {
    console.error("FetchAPI error", error);
    // Always return a safe object instead of throwing again
    return { status: 0, error: error?.message || "Network/parse error" };
  }
};
